import CloseButton from 'src/components/Buttons/CloseButton';
import DatePicker from 'src/components/DatePickers/Ranged';
import IconButton from 'src/components/Buttons/IconButton';
import Input from 'src/components/Forms/Input';
import Modal from '../../../utils/modal';
import moment from 'src/utils/moment';
import NormalButton from '../../Buttons/NormalButton';
import RadioInput from 'src/components/Forms/RadioInput';
import React, { useCallback } from 'react';
import Select from 'src/components/Forms/Select';
import useStyles from './style';
import { formatDate, getDayIndex, moveLastToFirstInArray, sortObjectKeys } from 'src/utils/useFunctions';
import { getUserSetting } from 'src/utils/useUser';
import { isCypress } from '../../../utils/useCypress';
import { setCalendarEventRepeatModal } from '../../../store/actions/modals.actions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';
import { weekdays } from 'src/constants/weekdays';

const CalendarEventRepeatModal: React.FunctionComponent = () => {

  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modalsData = useAppSelector((state: any) => state.modals);
  const userData = useAppSelector((state: any) => state.user);

  const selectedDate = moment(modalsData.calendarEventRepeatModal.date);
  const options = modalsData.calendarEventRepeatModal.options;
  const onSave = modalsData.calendarEventRepeatModal.onSave;

  const firstDayInWeek = getUserSetting(userData.userSettings, "customizations", ["calendar", "first_day_in_week"]);

  const weekdaysList: any = firstDayInWeek === 0 ? moveLastToFirstInArray(weekdays) : weekdays;

  const maxRepeat: any = {
    "day": 365,
    "week": 52,
    "month": 12,
  };

  const [state, setState] = useStates({
    repeatFrequencyCount: options.repeatFrequencyCount ? options.repeatFrequencyCount : 1,
    repeatFrequency: options.repeatFrequency ? options.repeatFrequency : "day",
    repeatDays: options.repeatDays ? options.repeatDays : [getDayIndex(weekdays, moment(selectedDate).format("dddd"))],
    repeatEnd: options.repeatEnd ? options.repeatEnd : 'never',
    repeatEndDate: options.repeatEndDate ? options.repeatEndDate : moment(selectedDate).add(1, 'month'),
    repeatEndCount: options.repeatEndCount ? options.repeatEndCount : 2,
  });

  const calendarRepeatFrequencies = useCallback(() => [
    {
      name: formatDate(state.repeatFrequencyCount, "day", t),
      value: 'day',
    },
    {
      name: formatDate(state.repeatFrequencyCount, "week", t),
      value: 'week',
    },
    {
      name: formatDate(state.repeatFrequencyCount, "month", t),
      value: 'month',
    },
  ], [state.repeatFrequencyCount]);

  const onCloseModal = () => {
    const settings = {
      isOpen: false,
      options: null,
      date: null,
      onSave: null,
    };
    dispatch(setCalendarEventRepeatModal(settings));
  };

  const handleClose = (e: any) => {
    e.stopPropagation();
    onCloseModal();
  };

  const handleOnDone = (e: any) => {
    let payload = {};
    if(state.repeatFrequency) {
      payload = {
        ...payload,
        repeatFrequency: state.repeatFrequency,
      }; 
    }
    if(state.repeatFrequencyCount) {
      payload = {
        ...payload,
        repeatFrequencyCount: state.repeatFrequencyCount,
      }; 
    }
    if(state.repeatFrequency === "week") {
      payload = {
        ...payload,
        repeatDays: state.repeatDays,
      }; 
    }
    if(state.repeatEnd) {
      payload = {
        ...payload,
        repeatEnd: state.repeatEnd,
      }; 
      if(state.repeatEnd === "atDay") {
        payload = {
          ...payload,
          repeatEndDate: state.repeatEndDate,
        }; 
      } else if(state.repeatEnd === "after") {
        payload = {
          ...payload,
          repeatEndCount: state.repeatEndCount,
        }; 
      }
    }
    payload = sortObjectKeys(payload);
    onSave(payload);
    handleClose(e);
  };

  const handleOnCancel = (e: any) => {
    handleClose(e);
  };

  const handleChangeRepeatFrequencyCount = (value: any) => {
    setState("repeatFrequencyCount", parseInt(value));
  };
  
  const handleChangeRepeatFrequency = (value: any) => {
    const val = value.value;
    setState("repeatFrequency", val);
  };

  const handleRepeatDay = (day: any) => {
    let newValue = state.repeatDays;
    if(newValue.length === 1 && newValue[0] === day) return;
    if(newValue.includes(day)) {
      newValue = newValue.filter((item: any) => item !== day);
    } else {
      newValue = [...newValue, day];
    }
    setState("repeatDays", newValue);
  };
  
  const handleChangeRepeatEnd = (value: any) => {
    setState("repeatEnd", value);
  };

  const handleChangeRepeatEndCount = (value: any) => {
    setState("repeatEndCount", parseInt(value));
  };

  const handleRepeatDate = (value: any) => {
    setState("repeatEndDate", value);
  };

  const getMaxRepeat = () => {
    return maxRepeat[state.repeatFrequency];
  };

  const dateRange = [moment(selectedDate), moment(selectedDate).add(365, 'days')];

  const canBeDone = ((state.repeatEnd === "after" && !state.repeatEndCount) || !state.repeatFrequencyCount) ? false : true;

  return (
    <Modal 
      open={true}
      onClose={onCloseModal}
    >
      <div className={classes.root} data-cy={isCypress() ? "calendarEventRepeatModal" : null}>
        <div className={classes.header}>
          <div className={classes.wrapper}>
            <p>{t('calendar_event_custom_repeat')}</p>
          </div>
          <CloseButton onClick={handleClose} dataCy="timesButton"/>
        </div>
        <div className={classes.body}>
          <div className={classes.repeatFrequencyWrapper}>
            <span>{t('calendar_repeat_every')}</span>
            <div className={classes.frequencyInputWrapper}>
              <Input className={classes.frequencyInput} type='number' min={1} max={getMaxRepeat()} useName={false} value={state.repeatFrequencyCount} numberControl="out" onChange={handleChangeRepeatFrequencyCount}/>
            </div>
            <Select className={classes.autoSelect} inputLabel={t("school")} items={calendarRepeatFrequencies()} defaultSelected="value" selected={state.repeatFrequency ? state.repeatFrequency : "day"} setSelected={handleChangeRepeatFrequency} width={200} allowClear={false}/>
          </div>
          {
            state.repeatFrequency === "week" ? (
              <div className={classes.repeatDaysWrapper}>
                {
                  weekdaysList.map((item: any, key: any) => (
                    <IconButton className={`${classes.dayButton} ${state.repeatDays.includes(getDayIndex(weekdays, item)) ? 'active' : null}`} key={`k_${key}`} onClick={() => handleRepeatDay(getDayIndex(weekdays, item))}>
                      {t(item.toLowerCase()).substring(0,1)}
                    </IconButton>
                  ))
                }
              </div>
            ) : state.repeatFrequency === "month" ? (
              <div className={classes.repeatMonthWrapper}>
                <span>{t('calendar_repeat_every')}</span>
                <Input className={classes.monthInput} value={t('calendar_repeat_day_in_month', {day: moment().format("D")})} readOnly={true}/>
              </div>
            ) : null
          }
          <div className={classes.repeatEndWrapper}>
            <span>{t('calendar_repeat_end')}</span>
            <div className={classes.option}>
              <RadioInput checked={state.repeatEnd === 'never'} label={`${t('calendar_repeat_never')}<small>(${t('calendar_repeat_never_info')})</small>`} onClick={() => handleChangeRepeatEnd('never')}/>
            </div>
            <div className={classes.option}>
              <RadioInput checked={state.repeatEnd === 'atDay'} label={t('calendar_repeat_at_day')} onClick={() => handleChangeRepeatEnd('atDay')}/>
              <DatePicker className={classes.datePicker} presetDateRange={dateRange} presetDate={state.repeatEndDate} setDate={handleRepeatDate} disabled={state.repeatEnd !== 'atDay'} allowReset={false}/>
            </div>
            <div className={classes.option}>
              <RadioInput checked={state.repeatEnd === 'after'} label={t('calendar_repeat_after')} onClick={() => handleChangeRepeatEnd('after')}/>
              <div className={classes.endInputWrapper}>
                <Input className={classes.endInput} append={t(state.repeatEndCount === 1 ? 'calendar_event_repeat' : 'calendar_event_repeats').toLowerCase()} type='number' min={1} max={getMaxRepeat()} useName={false} value={state.repeatEndCount} numberControl="out" onChange={handleChangeRepeatEndCount} disabled={state.repeatEnd !== 'after'}/>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.footer}>
          <NormalButton buttonType="secondary" onClick={handleOnCancel} data-cy={isCypress() ? "cancelButton" : null}>
            {t("cancel")}
          </NormalButton>
          <NormalButton buttonType="primary" onClick={handleOnDone} data-cy={isCypress() ? "doneButton" : null} disabled={!canBeDone}>
            {t("done")}
          </NormalButton>
        </div>
      </div>
    </Modal>
  );
};

export default CalendarEventRepeatModal;